import {APP_INITIALIZER, ErrorHandler, NgModule, APP_ID} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule, NoopAnimationsModule} from '@angular/platform-browser/animations';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {AuthInterceptor} from './guards/auth/auth.interceptor';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {MetaLoader, MetaModule, MetaStaticLoader, PageTitlePositioning} from '@src/@ngx-meta/core/src';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_DAYJS_DATE_ADAPTER_OPTIONS} from '@tabuckner/material-dayjs-adapter';
import {LocaleInterceptor} from './guards/locale.interceptor';
import {SharedModule} from './shared/shared.module';
import {MaintenanceInterceptor} from './guards/maintenance/maintenance.interceptor';
import {FeatureLockedInterceptor} from './guards/feature-locked.interceptor';
import {BrandUrlRewriteInterceptor} from './guards/brand-url-rewrite.interceptor';
import {APP_DATE_FORMATS, DayjsDateAdapter} from './shared/material-dayjs-adapter';
import {NotificationsModule} from './modules/notifications/notifications.module';
import {LoggingService} from '@app/services/util/logging.service';
import {environment} from '@src/environments/environment';
import {setupAppModule} from "@app/i18n";
import {GlobalErrorHandler} from "@app/util/global-error.handler";
import {BrandService} from '@app/services/brand.service';
import {MAT_DIALOG_DEFAULT_OPTIONS} from '@angular/material/dialog';
import {provideNgxMask} from 'ngx-mask';
import {ServiceWorkerModule} from '@angular/service-worker';

setupAppModule();

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NoopAnimationsModule,
    SharedModule,
    MetaModule.forRoot({
      provide: MetaLoader,
      useFactory: metaFactory,
      deps: [BrandService]
    }),
    NotificationsModule,
    ServiceWorkerModule.register('custom-sw.js', {
      enabled: environment.serviceWorker ?? false
    })
  ],
  providers: [
    LoggingService,
    {
      provide: APP_INITIALIZER,
      useFactory: (brandService: BrandService) => () => brandService.init(),
      deps: [BrandService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LocaleInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FeatureLockedInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BrandUrlRewriteInterceptor,
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: DayjsDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS]
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_DATE_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'sl'
    },
    {
      provide: MAT_DAYJS_DATE_ADAPTER_OPTIONS,
      useValue: {useUtc: false}
    },
    {
      provide: 'googleTagManagerId',
      useValue: environment.gtmID,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        maxWidth: '95vw',
        maxHeight: '90vh',
        width: '960px',
        hasBackdrop: true,
        panelClass: 'dialog'
      }
    },
    {
      provide: APP_ID,
      useValue: 'serverApp'
    },
    provideHttpClient(withInterceptorsFromDi()),
    // NGX-Mask global configuration, custom pattern naming conventions: https://jsdaddy.github.io/ngx-mask/
    provideNgxMask({
      patterns: {
        'U': {pattern: new RegExp('[A-Z]')},      // Uppercase letters only
        'N': {pattern: new RegExp('[0-9]')},      // Numbers only
        'X': {pattern: new RegExp('[A-Z0-9]')},   // Uppercase letters or numbers
        '1': {pattern: new RegExp('[1-9]')},      // Non-zero digits (1-9)
        '0': {pattern: new RegExp('[0-9]')},      // Any digits (1-9)
        'I': {pattern: new RegExp('[0Ii]')},      // 0, I, or i (for ID documents)
        'E': {pattern: new RegExp('[0Ee]')},      // 0, E, or e (for ID documents)
        'P': {pattern: new RegExp('[Pp]')},       // P or p (for passport)
        'A': {pattern: new RegExp('[A-Za-z0-9]')} // Any alphanumeric character
      }
    })
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}

export function metaFactory(brandService: BrandService): MetaLoader {
  const appName = brandService.config.appName;
  return new MetaStaticLoader({
    pageTitlePositioning: PageTitlePositioning.PrependPageTitle,
    pageTitleSeparator: ' | ',
    applicationName: appName,
    defaults: {
      title: appName,
      description: appName,
      'og:type': 'website',
    }
  });
}
